import {
  AllowanceSchema,
  ClassComparisonSchema,
  ClassObjectivesSchema,
  NextActivitySchema,
  ObjectiveSchema,
  RosterSchema,
  StudentDetailSchema,
  type ClassComparison,
  type ClassObjectives,
  type NextActivity,
  type Roster,
  type StudentDetail,
} from "@talent/protocol";
import { z } from "zod";

/**
 * The teacher's surface, and the one call the child's surface makes.
 *
 * Responses are PARSED, never cast, for the same reason as everywhere else in this client: a cast is
 * a claim about the server that nothing checks, and this data is painted onto a screen a teacher
 * makes decisions from.
 */

const BASE = import.meta.env["VITE_API_BASE"] ?? "";

/** Refusals the roster screen acts on differently. */
export type ClassFailure =
  | "unauthenticated"
  | "forbidden"
  | "phone-taken"
  | "bad-phone"
  /** A live class of this teacher already has that name. */
  | "class-name-taken"
  /** The only class left, which cannot be closed. */
  | "last-class"
  /**
   * No room left in the teacher's allowance, which only an operator can change.
   *
   * Told apart from every other refusal because it is the one a teacher cannot resolve by retyping
   * anything: the answer is a number somebody else set, so the message has to name it rather than
   * suggest they try again.
   */
  | "class-limit"
  | "student-limit"
  | "unknown";

export class ClassError extends Error {
  constructor(
    readonly failure: ClassFailure,
    /**
     * The limit that was reached, on the two refusals that carry one.
     *
     * Read from the response rather than from the allowance the screen is holding: the screen's copy can
     * be a few seconds old, and the number in the sentence explaining a refusal must be the number that
     * caused it.
     */
    readonly limit: number | null = null,
  ) {
    super(failure);
    this.name = "ClassError";
  }
}

const RefusalSchema = z.object({
  code: z.string().optional(),
  limit: z.number().int().nonnegative().optional(),
});

async function refusalFor(res: Response): Promise<ClassError> {
  if (res.status === 401) return new ClassError("unauthenticated");
  const parsed = RefusalSchema.safeParse(await res.json().catch(() => ({})));
  const code = parsed.success ? parsed.data.code : undefined;
  const limit = parsed.success ? parsed.data.limit ?? null : null;
  if (code === "phone-taken") return new ClassError("phone-taken");
  if (code === "bad-phone") return new ClassError("bad-phone");
  if (code === "class-name-taken") return new ClassError("class-name-taken");
  if (code === "last-class") return new ClassError("last-class");
  if (code === "class-limit") return new ClassError("class-limit", limit);
  if (code === "student-limit") return new ClassError("student-limit", limit);
  if (res.status === 403) return new ClassError("forbidden");
  return new ClassError("unknown");
}

/**
 * `unknown` as the schema's INPUT type, deliberately.
 *
 * `z.ZodType<T>` defaults its input to `T`, so a schema whose parsed output differs from its input —
 * anything with a `.default()`, which `Objective.requires` has — binds `T` to the input shape and the
 * return type comes out with optional fields the caller then has to re-check. Fixing the input to
 * `unknown` makes `T` the parsed result, which is what every caller actually receives.
 */
async function get<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { accept: "application/json" },
    credentials: "same-origin",
  });
  if (!res.ok) throw await refusalFor(res);
  return schema.parse(await res.json());
}

/**
 * Everything a teacher reads is scoped to ONE class (ADR-012).
 *
 * The class id is a parameter on every one of these rather than something the server infers from the
 * session, because a teacher with a morning and an afternoon group has two answers and only they know
 * which one they are looking at. A server that guessed would show a median across both — a median of
 * nothing in particular.
 */
export const fetchRoster = (classId: string): Promise<Roster> =>
  get(`/v1/classes/${classId}/roster`, RosterSchema);

/** What the signed-in CHILD should play next. The only class call a student may make. */
export const fetchMyNext = (): Promise<NextActivity> =>
  get("/v1/me/next", NextActivitySchema);

export const fetchStudent = (studentId: string): Promise<StudentDetail> =>
  get(`/v1/students/${studentId}/progress`, StudentDetailSchema);

/** What one student should play next. Used after assigning, to show the effect immediately. */
export const fetchStudentNext = (studentId: string): Promise<NextActivity> =>
  get(`/v1/students/${studentId}/next`, NextActivitySchema);

export const fetchClassObjectives = (classId: string): Promise<ClassObjectives> =>
  get(`/v1/classes/${classId}/objectives`, ClassObjectivesSchema);

export const fetchComparison = (classId: string): Promise<ClassComparison> =>
  get(`/v1/classes/${classId}/comparison`, ClassComparisonSchema);

/**
 * The objective catalogue, with the level windows and the suggested order.
 *
 * Served from the pinned bundle rather than assembled here, so the list a teacher assigns from is
 * exactly the list the child's games are cut into.
 */
const CatalogueSchema = z.object({
  bundleVersion: z.string().min(1),
  objectives: z.array(ObjectiveSchema),
});
/**
 * Inferred from the schema rather than written out.
 *
 * `Objective.requires` has a Zod default, which makes it optional on the way IN and present on the
 * way out — so a hand-written interface saying `requires: string[]` does not match what `parse`
 * returns under `exactOptionalPropertyTypes`. Inferring keeps one definition.
 */
export type Catalogue = z.infer<typeof CatalogueSchema>;

export const fetchObjectives = (): Promise<Catalogue> =>
  get("/v1/objectives", CatalogueSchema);

async function send(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<Response> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
    credentials: "same-origin",
  });
  if (!res.ok) throw await refusalFor(res);
  return res;
}

const AssignedSchema = z.object({
  objectiveId: z.string().min(1),
  assigned: z.number().int().nonnegative(),
  assignmentIds: z.array(z.string().uuid()),
});

/**
 * Gives one objective to named students, or to a whole class.
 *
 * "all" is resolved on the server from the named class's own roster, so a class-wide goal cannot reach
 * a child this teacher does not teach — and a named id outside that class is refused rather than
 * quietly dropped, because "done" and "you cannot do that" are different answers.
 */
export async function assignObjective(input: {
  objectiveId: string;
  classId: string;
  students: "all" | string[];
  note?: string | null;
}): Promise<z.infer<typeof AssignedSchema>> {
  const res = await send("/v1/assignments", "POST", {
    objectiveId: input.objectiveId,
    classId: input.classId,
    students: input.students,
    note: input.note ?? null,
  });
  return AssignedSchema.parse(await res.json());
}

export async function withdrawAssignment(assignmentId: string): Promise<void> {
  await send(`/v1/assignments/${assignmentId}`, "DELETE");
}

/**
 * Withdraws every live goal in a class, and reports how many there were.
 *
 * The count is used rather than ignored: "nothing was set" and "eleven goals withdrawn" are
 * different things to have just done, and a screen that said the same for both would leave a
 * teacher unsure whether the button worked.
 */
export async function resetClassAssignments(classId: string): Promise<number> {
  const res = await send(`/v1/classes/${classId}/assignments`, "DELETE");
  const body = z.object({ withdrawn: z.number().int().nonnegative() }).parse(await res.json());
  return body.withdrawn;
}

export async function renameStudent(
  studentId: string,
  input: { firstName: string; lastName: string },
): Promise<void> {
  await send(`/v1/students/${studentId}`, "PATCH", input);
}

/**
 * Removes a student from the class.
 *
 * Their name and number are erased and their learning record is kept (R-7). Irreversible, which is why
 * the screen asks twice before calling it.
 */
export async function removeStudent(studentId: string): Promise<void> {
  await send(`/v1/students/${studentId}`, "DELETE");
}

const CreatedStudentSchema = z.object({
  studentId: z.string().uuid(),
  phone: z.string().min(1),
  bundleVersion: z.string().min(1),
});

export async function addStudent(
  classId: string,
  input: { firstName: string; lastName: string; phone: string },
): Promise<z.infer<typeof CreatedStudentSchema>> {
  const res = await send(`/v1/classes/${classId}/students`, "POST", input);
  return CreatedStudentSchema.parse(await res.json());
}

/**
 * The teacher's classes, and making, renaming and closing them.
 *
 * `students` comes back with each one so the switcher can say how big a class is without a request per
 * class — a teacher choosing between two group names months later needs something more than the names.
 */
const ClassListSchema = z.object({
  classes: z.array(
    z.object({
      classId: z.string().uuid(),
      name: z.string().min(1),
      students: z.number().int().nonnegative(),
    }),
  ),
  /**
   * How much of their allowance this teacher has used.
   *
   * Comes back with the list rather than from a request of its own, because the screen that lists classes
   * is the screen that offers to make one — two requests would draw the button and then take it away.
   */
  allowance: AllowanceSchema,
});
export type ClassList = z.infer<typeof ClassListSchema>["classes"];
export type ClassListing = z.infer<typeof ClassListSchema>;

export const fetchClasses = (): Promise<ClassListing> =>
  get("/v1/classes", ClassListSchema);

/**
 * Children whose class was closed.
 *
 * They belong to no live class, so they appear in no roster — this is the only place they are visible
 * and `placeStudent` is how they get back into one.
 */
const UnplacedSchema = z.object({
  students: z.array(
    z.object({
      studentId: z.string().uuid(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      formerClassName: z.string(),
    }),
  ),
});
export type Unplaced = z.infer<typeof UnplacedSchema>["students"];

export const fetchUnplaced = (): Promise<Unplaced> =>
  get("/v1/students/unplaced", UnplacedSchema).then((r) => r.students);

/** Puts an existing student into a class. Both ends are checked on the server. */
export async function placeStudent(classId: string, studentId: string): Promise<void> {
  await send(`/v1/classes/${classId}/students/${studentId}`, "POST");
}

/**
 * Which games a class may play (ADR-014).
 *
 * Every game comes back, open or not: the screen is a list of switches, and a teacher choosing what to
 * open next has to see the ones that are shut.
 */
const ClassGamesSchema = z.object({
  bundleVersion: z.string().min(1),
  games: z.array(
    z.object({
      taskId: z.string().min(1),
      family: z.string().min(1),
      open: z.boolean(),
    }),
  ),
});
export type ClassGames = z.infer<typeof ClassGamesSchema>["games"];

export const fetchClassGames = (classId: string): Promise<ClassGames> =>
  get(`/v1/classes/${classId}/games`, ClassGamesSchema).then((r) => r.games);

export async function setGameOpen(
  classId: string,
  taskId: string,
  open: boolean,
): Promise<void> {
  await send(`/v1/classes/${classId}/games/${taskId}`, "PUT", { open });
}

/** The games the signed-in CHILD may play. Ids only — they already have the bundle. */
const MyGamesSchema = z.object({ open: z.array(z.string().min(1)) });

export const fetchMyGames = (): Promise<string[]> =>
  get("/v1/me/games", MyGamesSchema).then((r) => r.open);

const CreatedClassSchema = z.object({
  classId: z.string().uuid(),
  name: z.string().min(1),
});

export async function createClass(name: string): Promise<z.infer<typeof CreatedClassSchema>> {
  const res = await send("/v1/classes", "POST", { name });
  return CreatedClassSchema.parse(await res.json());
}

export async function renameClass(classId: string, name: string): Promise<void> {
  await send(`/v1/classes/${classId}`, "PATCH", { name });
}

/**
 * Closes a class. Soft: it stops appearing, and what the children did remains.
 *
 * Refused for a teacher's last class, since a teacher with none has no screen that works — the caller
 * gets `last-class` and says so rather than reporting a failure.
 */
export async function closeClass(classId: string): Promise<void> {
  await send(`/v1/classes/${classId}`, "DELETE");
}
